// /r/:slug → public TapNConnect review-request landing page.
// NFC-friendly: big tap target. Counts the visit, shows headline + CTA,
// 1-click jumps to Google's review composer (or whatever review_url was set).
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, ExternalLink, Loader2 } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function TapNConnect() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/r/${encodeURIComponent(slug)}`);
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setErr(d?.detail || "Page not found"); return; }
        setPage(d);
      } catch (e) { setErr(e?.message || "Network error"); }
    })();
  }, [slug]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-center px-4" data-testid="tapnconnect-error">
        <div>
          <div className="text-rose-600 font-bold mb-2">{err}</div>
          <a href="/" className="text-sm underline text-slate-600">Go to homepage</a>
        </div>
      </div>
    );
  }
  if (!page) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2"/>Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-emerald-50 flex items-center justify-center px-4" data-testid="tapnconnect-page">
      <div className="max-w-md w-full text-center">
        <div className="flex justify-center gap-1 mb-4" aria-hidden>
          {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-6 h-6 text-amber-500" fill="currentColor"/>)}
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700 mb-2">{page.property_label}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
          {page.headline}
        </h1>
        <p className="text-base text-slate-600 mt-3 leading-relaxed">{page.subheadline}</p>
        <a
          href={page.review_url}
          data-testid="tapnconnect-cta"
          target="_blank" rel="noreferrer"
          className="inline-flex items-center justify-center gap-2 mt-8 w-full sm:w-auto px-8 py-4 rounded-2xl bg-violet-700 hover:bg-violet-800 text-white text-base font-bold shadow-lg shadow-violet-500/30 transition-colors"
        >
          {page.cta_text || "Leave a Google review"} <ExternalLink className="w-4 h-4"/>
        </a>
        <div className="mt-6 text-xs text-slate-400">Takes about 30 seconds. Thank you — every word helps.</div>
      </div>
    </div>
  );
}
